import Relay from 'react-relay';

export const productTrackingFragment = Relay.QL`
  fragment on Product {
    publicId
    category {
      publicId
      name
      parent {
        publicId
        name
      }
    }
    country
    pictures {
      id
    }
    approximateAddress
    price {
      display
      internationalShipping
      localShipping
    }
    quantity
    variantSet {
      publicId
      name
    }
    variantOptions {
      variant {
        publicId
        name
      }
      quantity
    }
  }
`;

function productToMixpanelData(product) {
  return {
    'category id': product.category.publicId,
    'category name': product.category.name,
    'category path': product.category.parent
      ? `${product.category.parent.name} / ${product.category.name}`
      : product.category.name,
    country: product.country,
    facebook: 'no',
    // gender
    hand_delivery: 'no',
    images: product.pictures.length,
    instagram: 'no',
    location: product.approximateAddress,
    national_shipping: product.price.localShipping ? 'yes' : 'no',
    paypal: 'yes',
    price: product.price.display,
    quantity: product.quantity
      ? product.quantity
      : product.variantOptions.reduce(
          (acc, { quantity }) => (acc += quantity),
          0,
        ),
    sendNewsletter: 'no',
    'size ids': product.variantOptions
      ? product.variantOptions.map(opt => opt.variant.publicId)
      : null,
    'size names': product.variantOptions
      ? product.variantOptions.map(opt => opt.variant.name)
      : null,
    twitter: 'no',
    'variant name': product.variantSet ? product.variantSet.name : null,
    'variant set id': product.variantSet ? product.variantSet.publicId : null,
    worldwide_shipping: product.price.internationalShipping ? 'yes' : 'no',
  };
}

export default class Mixpanel {
  constructor() {
    if (typeof window === 'undefined') {
      return;
    }

    if (__DEV__) {
      window.mixpanel = {
        identify(params) {
          console.groupCollapsed(`[Mixpanel] Identify`);
          console.debug(params);
          console.groupEnd();
        },
        track(name, params) {
          console.groupCollapsed(`[Mixpanel] Event: ${name}`);
          console.debug(params);
          console.groupEnd();
        },
        reset() {
          console.debug(`[Mixpanel] Reset`);
        },
      };
    }
    this.context = {
      user: {},
    };
  }

  identifyUser({ id, username }) {
    this.context = {
      ...this.context,
      user: {
        username,
        id,
      },
    };

    window.mixpanel.identify(id);
  }

  unidentifyUser() {
    window.mixpanel.reset();
    this.context = {
      ...this.context,
      user: {},
    };
  }

  trackLogin({ id, username }) {
    window.mixpanel.track('confirm signin', {
      $username: username,
    });
  }

  trackLogout() {
    window.mixpanel.track('signout');
  }

  trackBuyProduct({
    username,
    id,
    slug,
    price,
    currency,
    source,
    similarItemIndex,
    branch_channel,
    deeplink_signup,
  }) {
    window.mixpanel.track('buy product', {
      $username: this.context.user.username,
      id: this.context.user.id,
      'product owner': username,
      product: id,
      slug: slug,
      price: price,
      currency: currency,
      from: source,
      branch_channel: branch_channel,
      deeplink_signup: deeplink_signup,
      'similar item index': similarItemIndex,
    });
  }

  trackLikeProduct({
    username,
    id,
    slug,
    price,
    currency,
    source,
    similarItemIndex,
  }) {
    const isMine = this.context.user.username === username;

    window.mixpanel.track('like product', {
      $username: this.context.user.username,
      id: this.context.user.id,
      'product owner': username,
      product: id,
      slug: slug,
      price: price,
      currency: currency,
      isMine: isMine ? 'yes' : 'no',
      from: source,
      'similar item index': similarItemIndex,
    });
  }

  trackUnlikeProduct({
    username,
    id,
    slug,
    price,
    currency,
    source,
    similarItemIndex,
  }) {
    const isMine = this.context.user.username === username;

    window.mixpanel.track('unlike product', {
      $username: this.context.user.username,
      id: this.context.user.id,
      'product owner': username,
      product: id,
      slug: slug,
      price: price,
      currency: currency,
      isMine: isMine ? 'yes' : 'no',
      from: source,
      'similar item index': similarItemIndex,
    });
  }

  trackFollowUser({ username }) {
    window.mixpanel.track('follow user', {
      $username: this.context.user.username,
      id: this.context.user.id,
      by: this.context.user.username,
      to: username,
    });
  }

  trackUnfollowUser({ username }) {
    window.mixpanel.track('unfollow user', {
      $username: this.context.user.username,
      id: this.context.user.id,
      by: this.context.user.username,
      to: username,
    });
  }

  trackProductSearch({ query }) {
    window.mixpanel.track('execute products search', {
      $username: this.context.user.username,
      id: this.context.user.id,
      query: query,
    });
  }

  trackPasswordResetRequest({ identifier }) {
    window.mixpanel.track('request password reset', {
      identifier: identifier,
    });
  }

  trackEditShop() {
    window.mixpanel.track('edit shop', {
      $username: this.context.user.username,
    });
  }

  trackSaveEditShop() {
    window.mixpanel.track('save shop', {
      $username: this.context.user.username,
    });
  }

  trackCancelEditShop() {
    window.mixpanel.track('cancel edit shop', {
      $username: this.context.user.username,
    });
  }

  moveSoldItemsToBottom() {
    window.mixpanel.track('move sold items to bottom', {
      $username: this.context.user.username,
    });
  }

  trackBeginListingFlow() {
    window.mixpanel.track('add new product', {
      $username: this.context.user.username,
      id: this.context.user.id,
      // country: null, // two-letter
      // gender: null, // m/f/o
      sendNewsletter: 'no',
    });
  }

  trackConfirmListing({ product }) {
    window.mixpanel.track('confirm product creation', {
      $username: this.context.user.username,
      id: this.context.user.id,
      ...productToMixpanelData(product),
    });
  }

  trackConfirmListingAndAddAnother({ product }) {
    window.mixpanel.track('confirm product creation', {
      $username: this.context.user.username,
      id: this.context.user.id,
      addAnother: 'yes',
      ...productToMixpanelData(product),
    });
  }

  trackDownLoadAppHeader({ source }) {
    window.mixpanel.track('download app button', {
      from: source
    });
  }

  trackShowAB({ testId, variantId }) {
    window.mixpanel.track('Web experiment started', {
      'Web test ID': testId,
      'Web variant ID': variantId
    });
  }

  trackActionAB({ testId, variantId, button }) {
    window.mixpanel.track('Web experiment variant action', {
      'Web test ID': testId,
      'Web variant ID': variantId,
      'Web action button': button
    });
  }

  trackProfileGetTheApp({ username }) {
    window.mixpanel.track('Profile download banner', {
      'Profile': username
    });
  }

  trackVideoPlay() {
    window.mixpanel.track('product play video');
  }

}



// WEBPACK FOOTER //
// ../src/client/utils/tracking/Mixpanel.js